/**
 * Server functions da central de ações (/admin/campanhas).
 *  - getAcoesStateFn: público (loaders SSR da home e das LPs das ações)
 *  - setAcaoAtiva / listAcaoChecklist / toggleChecklistItem: apenas admin
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "@/lib/admin/auth.server";
import {
  ACOES,
  CHECKLIST_ITEMS,
  acaoStateKey,
  getAcao,
  type AcoesState,
} from "@/lib/campanhas/acoes";
import { readAcoesStateServer } from "@/lib/campanhas/acoes.server";
import { ACTIVE_CAMPAIGN_KEY, type ActiveCampaign } from "@/lib/checkout/campanhas";
import { BANNER_CONFIG_KEY, normalizeBannerConfig } from "@/lib/home/banner";
import { readBannerConfigServer } from "@/lib/home/banner.server";

/** Público — usado nos loaders SSR da home, /plantaofeo e /flashfeo. */
export const getAcoesStateFn = createServerFn({ method: "GET" }).handler(
  async (): Promise<AcoesState> => readAcoesStateServer(),
);

const SetSchema = z.object({
  slug: z.enum(["plantaofeo", "flashfeo", "semanadafisio", "black2x1", "certquadril"]),
  ativa: z.boolean(),
});

export const setAcaoAtiva = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => SetSchema.parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "/admin/campanhas");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const acao = getAcao(data.slug);
    if (!acao) throw new Error("Ação não encontrada");

    const now = new Date().toISOString();

    // 1) log da ativação/desativação
    const { data: logRow, error: logErr } = await supabaseAdmin
      .from("campaign_activation_log")
      .insert({
        action: data.ativa ? "ativar" : "desativar",
        campaign_slug: acao.slug,
        campaign_nome: acao.nome,
        cupom: acao.cupom ?? "-",
        is_custom: false,
        mirror_route: acao.mirrorPos ? acao.lp : null,
        metadata: { source: "acao" },
        created_by: context.userId,
      })
      .select("id")
      .single();
    if (logErr) throw new Error(logErr.message);
    const activationId = logRow?.id ?? null;

    // 2) estado da ação
    const { error: stateErr } = await supabaseAdmin.from("site_config").upsert(
      {
        key: acaoStateKey(acao.slug),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        value: {
          ativa: data.ativa,
          ativadoEm: data.ativa ? now : null,
          activationId,
        } as any,
        updated_by: context.userId,
      },
      { onConflict: "key" },
    );
    if (stateErr) throw new Error(stateErr.message);

    // 3) chave legada usada pelo pré-checkout e por /pos (troca de cupom + LP espelho)
    if (acao.cupom) {
      if (data.ativa) {
        const campaign: ActiveCampaign = {
          slug: acao.slug,
          nome: acao.nome,
          cupom: acao.cupom,
          cupons: acao.cupons,
          mirrorRoute: acao.mirrorPos ? acao.lp : undefined,
          isCustom: false,
          ativadoEm: now,
        };
        const { error } = await supabaseAdmin.from("site_config").upsert(
          {
            key: ACTIVE_CAMPAIGN_KEY,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            value: campaign as any,
            updated_by: context.userId,
          },
          { onConflict: "key" },
        );
        if (error) throw new Error(error.message);
      } else {
        await supabaseAdmin.from("site_config").delete().eq("key", ACTIVE_CAMPAIGN_KEY);
      }
    }

    // 3.1) banner rotativo da home: slide da ação em 1ª posição
    if (acao.slideKey) {
      const config = await readBannerConfigServer();
      const enabled = { ...config.enabled };
      let order = config.order.filter((k) => k !== acao.slideKey);

      if (data.ativa) {
        enabled[acao.slideKey] = true;
        order = [acao.slideKey, ...order];
        if (acao.hidesSlideKey) enabled[acao.hidesSlideKey] = false;
      } else {
        enabled[acao.slideKey] = false;
        order = [...order, acao.slideKey];
        if (acao.hidesSlideKey) enabled[acao.hidesSlideKey] = true;
      }

      const { error: bannerErr } = await supabaseAdmin.from("site_config").upsert(
        {
          key: BANNER_CONFIG_KEY,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          value: normalizeBannerConfig({ order, enabled }) as any,
          updated_by: context.userId,
        },
        { onConflict: "key" },
      );
      if (bannerErr) throw new Error(bannerErr.message);
    }

    // 4) checklist manual da fase correspondente
    const phase = data.ativa ? "inicio" : "fim";
    if (activationId) {
      await supabaseAdmin.from("campaign_checklist").insert(
        (acao.checklist ?? CHECKLIST_ITEMS)[phase].map((it) => ({
          activation_id: activationId,
          action_slug: acao.slug,
          phase,
          item_key: it.key,
          item_label: it.label,
        })),
      );
    }

    return {
      ok: true,
      activationId,
      passos: data.ativa ? acao.passosAtivar : acao.passosDesativar,
    };
  });

export type ChecklistRow = {
  id: string;
  action_slug: string;
  phase: "inicio" | "fim";
  item_key: string;
  item_label: string;
  done: boolean;
  done_at: string | null;
  created_at: string;
};

/** Checklist da ativação/desativação mais recente de cada ação. */
export const listAcoesChecklist = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<Record<string, ChecklistRow[]>> => {
    await assertAdmin(context.userId, "/admin/campanhas");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const out: Record<string, ChecklistRow[]> = {};
    for (const acao of ACOES) {
      const { data } = await supabaseAdmin
        .from("campaign_checklist")
        .select("id, action_slug, phase, item_key, item_label, done, done_at, created_at, activation_id")
        .eq("action_slug", acao.slug)
        .order("created_at", { ascending: false })
        .limit(20);
      const rows = (data ?? []) as Array<ChecklistRow & { activation_id: string | null }>;
      const latest = rows[0]?.activation_id ?? null;
      out[acao.slug] = rows
        .filter((r) => r.activation_id === latest)
        .sort((a, b) => a.created_at.localeCompare(b.created_at));
    }
    return out;
  });

export const toggleChecklistItem = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ id: z.string().uuid(), done: z.boolean() }).parse(d))
  .handler(async ({ data, context }) => {
    await assertAdmin(context.userId, "/admin/campanhas");
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error } = await supabaseAdmin
      .from("campaign_checklist")
      .update({
        done: data.done,
        done_by: data.done ? context.userId : null,
        done_at: data.done ? new Date().toISOString() : null,
      })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
